import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {Layout, Type} from '@republic/bits';
import {map} from '@republic/foundation/lang/array';
import {compose, noop} from '@republic/foundation/lang/function';
import {owns} from '@republic/foundation/lang/object';
import {createComponent, create, use} from '@republic/react-foundation';
import AuthStream from '../../auth/streams/AuthStream';
import Copy from '../../copy/components/Copy';
import Button from '../../core/components/Button';
import ButtonLink from '../../core/components/ButtonLink';
import colors from '../../core/components/colors';
import LoadingSpinner from '../../core/components/LoadingSpinner';
import DropdownContainer from '../../core/containers/DropdownContainer';
import {event as reportEvent} from '../../core/services/analytics';
import {relative} from '../../core/services/time';
import ProductImage from '../../products/components/ProductImage';
import ProductStream from '../../products/streams/ProductStream';
import UserStream from '../../users/streams/UserStream';
import submitCancellation from '../services/submit-cancellation';
import CancellationOffersStream from '../streams/CancellationOffersStream';
import SubscribersStream from '../streams/SubscribersStream';
import {subscriberType} from '../types';
import Label from '../../copy/components/Label';

const
    business = user => {
        const {data: {business = null} = {}} = user || {};

        return !!business;
    },
    CancellationSelection = (
        compose(
            create(
                CancellationOffersStream,
                ({line}) => ({line}),
                {
                    propTypes: {
                        line: PropTypes.string.isRequired
                    }
                }),
            use({
                    auth: AuthStream,
                    offers: CancellationOffersStream,
                    subscribers: SubscribersStream
                },
                ({auth, offers, subscribers}, {auth: ignore1, offers: ignore2, subscribers: ignore3, ...props}) => ({
                    ...props,
                    offers,
                    fetching: !!(offers && offers.fetching),
                    submit: (
                        (auth && auth.token && auth.id && offers && offers.data) ?
                            () => submitCancellation(auth, offers) :
                            noop),
                    clear: (
                        (offers && subscribers) ?
                            () => {
                                offers.clear();
                                subscribers.clear();
                            } :
                            noop)
                })))(
            createComponent(
                'CancellationSelection',
                {
                    propTypes: {
                        offers: PropTypes.object,
                        fetching: PropTypes.bool.isRequired,
                        submit: PropTypes.func.isRequired,
                        clear: PropTypes.func.isRequired,
                        line: PropTypes.string.isRequired,
                        complete: PropTypes.func.isRequired
                    }
                },
                class CancellationSelection extends Component {
                    constructor(props) {
                        super(props);
                        this.state = {disabled: false};
                        this.mounted = false;
                    }

                    componentDidMount() {
                        this.mounted = true;
                    }

                    componentWillUnmount() {
                        this.mounted = false;
                    }

                    render() {
                        const
                            {
                                offers,
                                fetching,
                                submit
                            } = this.props,
                            {
                                disabled
                            } = this.state;

                        return (
                            fetching ?
                                <Layout align="center" padding={{bottom: 1}}><LoadingSpinner /></Layout> :
                                (offers && offers.data && offers.data.length) ?
                                    <Layout align="center" padding={{bottom: 1}}>
                                        <DropdownContainer>
                                            {({toggle, opened}) => (
                                                opened ?
                                                    <>
                                                        <Layout padding={{bottom: 0.8}}>
                                                            <Label setting="portal_medium">
                                                                Confirm my cancellation
                                                            </Label>
                                                        </Layout>
                                                        <Button
                                                            disabled={disabled}
                                                            label="Confirm"
                                                            width="200px;"
                                                            action={() => {
                                                                this.setState(
                                                                    {disabled: true},
                                                                    () => {
                                                                        (submit()
                                                                        .then(data => {
                                                                            const {line, complete, clear} = this.props;

                                                                            if (data && data.order_number) {
                                                                                complete(line, data.order_number);
                                                                            }
                                                                            clear();
                                                                            if (this.mounted) {
                                                                                this.setState({disabled: false});
                                                                            }
                                                                        })
                                                                        .catch(() => {
                                                                            if (this.mounted) {
                                                                                this.setState({disabled: false});
                                                                            }
                                                                        }));
                                                                    });
                                                            }} />
                                                        {disabled &&
                                                            <Layout row justify="center" padding={{top: 1, bottom: 0.3}}>
                                                                <LoadingSpinner />
                                                                <Label setting="portal_medium">Cancelling...</Label>
                                                            </Layout>}
                                                    </> :
                                                    <Layout align="center" padding={{top: 1}}>
                                                        <Button action={toggle} label="Cancel Service" width="200px" disabled={disabled} />
                                                    </Layout>)}
                                        </DropdownContainer>
                                    </Layout> :
                                    <Layout padding={{bottom: 1}}>
                                        <Copy setting="tiny" align="center">We're unable to process your request at this time because there's a pending cancellation for this device.</Copy>
                                    </Layout>);
                    }
                })));

export default (
    use({
            subscribers: SubscribersStream,
            user: UserStream
        },
        ({subscribers, user}, {subscribers: ignore1, user: ignore2, ...props}) => ({
            ...props,
            subscribers,
            business: business(user)
        }))(
        createComponent(
            'Cancellation',
            {
                propTypes: {
                    subscribers: (
                        PropTypes.shape({
                            fetching: PropTypes.bool,
                            data: (
                                PropTypes.arrayOf(
                                    PropTypes.shape(
                                        subscriberType)
                                    .isRequired))
                        }))
                }
            },
            class Cancellation extends Component {
                constructor(props) {
                    super(props);
                    this.state = {transactions: {}};
                }

                complete = (line, transaction) => {
                    this.setState(({transactions}) => ({
                        transactions: {
                            ...transactions,
                            [line]: transaction
                        }
                    }));
                }

                render() {
                    const
                        {subscribers, business} = this.props,
                        {transactions} = this.state,
                        {complete} = this;

                    return (
                        (!subscribers || subscribers.fetching) ?
                            <Layout align="center"><LoadingSpinner /></Layout> :
                            (subscribers && subscribers.data && subscribers.data.length && !business) ?
                                map(
                                    subscribers.data,
                                    subscriber => (
                                        map(
                                            subscriber.devices,
                                            (device, i) => {
                                                const
                                                    end = (
                                                        device.prepay.end ?
                                                            new Date(device.prepay.end) :
                                                            null);
                                                return (
                                                    <Layout key={device.id}>
                                                        <Layout row justify="between" align="start" padding={{bottom: 1}} margin={{bottom: 1}}>
                                                            <Layout padding={{right: 1}}>
                                                                <ProductStream sku={device.sku}>
                                                                    <ProductImage image="thumb" alignSelf="center" />
                                                                </ProductStream>
                                                            </Layout>
                                                            <Layout>
                                                                <Layout padding={{top: 1}}>
                                                                    <Layout padding={{bottom: 0.3}}>
                                                                        <Label setting="portal_medium">
                                                                            <Type weight="bold" tag="span">Name: </Type>{device.name}
                                                                        </Label>
                                                                    </Layout>
                                                                    <Layout padding={{bottom: 0.3}}>
                                                                        <Label setting="portal_medium">
                                                                            <Type weight="bold" tag="span">Device: </Type>{device.id}
                                                                        </Label>
                                                                    </Layout>
                                                                </Layout>
                                                            </Layout>
                                                        </Layout>
                                                        <Layout position="relative">
                                                            <Layout padding={{bottom: 2}} margin={{bottom: 2}} border={{width: [0, 0, 1, 0], color: colors.gray}}>
                                                                {subscribers.fetching ?
                                                                    <Layout align="center"><LoadingSpinner /></Layout> :
                                                                    <Layout flex>
                                                                        {device.warranty && !(subscriber.line in transactions) && (!device.prepay.active || device.prepay.cancellable) &&
                                                                            <Layout padding={{bottom: 1}}>
                                                                                <Copy setting="tiny" align="center">
                                                                                    Since you're within the allowed return period, you may be eligible for a refund.
                                                                                </Copy>
                                                                            </Layout>}
                                                                        {device.prepay.active && !device.prepay.cancellable && !(subscriber.line in transactions) && end &&
                                                                            <Layout padding={{bottom: 1}}>
                                                                                <Copy setting="tiny" align="center">
                                                                                    This Relay is currently on the annual payment plan. If you wish to cancel, you <Type tag="span" weight="bold">will not</Type> be given a prorated refund. Your annual plan ends <Type tag="span" weight="bold">{relative(end)}</Type>. To proceed with cancelling, click&nbsp;below.
                                                                                </Copy>
                                                                            </Layout>}
                                                                        {!owns(transactions, subscriber.line) ?
                                                                            <CancellationSelection line={subscriber.line} complete={complete} /> :
                                                                            <Layout>
                                                                                {(device.relaygo_purchase && device.warranty) ?
                                                                                    <Layout padding={{bottom: 1}}>
                                                                                        <Copy setting="tiny" align="center">
                                                                                            We appreciate you trying Relay! Your service has been cancelled for this device. Your cancellation transaction # is <Type tag="span" weight="bold">{`${transactions[subscriber.line]}`}</Type>. You are within your Money Back Guarantee period. You will receive a refund of the service fee paid at activation. Please allow <Type tag="span" weight="bold">3-5 business days</Type> for this to post to the card associated to your account. You will also receive details via email to return your Relay device.
                                                                                        </Copy>
                                                                                    </Layout> :
                                                                                    (!device.relaygo_purchase && device.warranty) ?
                                                                                        <Layout padding={{bottom: 1}}>
                                                                                            <Copy setting="tiny" align="center">
                                                                                                Your service has been cancelled for this device. Your cancellation transaction # is <Type tag="span" weight="bold">{`${transactions[subscriber.line]}`}</Type>. You are within your Money Back Guarantee period and will receive a refund of the service fee paid at activation. Please allow <Type tag="span" weight="bold">3-5 business days</Type> for this to post to the card associated to your account. We appreciate you trying Relay! If you wish to reactivate in the future please contact the Relay support team.
                                                                                            </Copy>
                                                                                        </Layout> :
                                                                                        <Layout padding={{bottom: 1}}>
                                                                                            <Copy setting="tiny" align="center">
                                                                                                Your service has been cancelled for this device. Your cancellation transaction # is <Type tag="span" weight="bold">{`${transactions[subscriber.line]}`}</Type>. We appreciate you trying Relay! If you wish to reactivate in the future please contact the Relay support team.
                                                                                            </Copy>
                                                                                        </Layout>}
                                                                                <>
                                                                                    <Layout padding={{top: 2, bottom: 1}}>
                                                                                        <Copy setting="body" weight="bold" align="center">
                                                                                            Your reason for cancelling is very important to us. Please provide your feedback in this 1-minute survey.
                                                                                        </Copy>
                                                                                    </Layout>
                                                                                    <ButtonLink
                                                                                        label="Fill out survey"
                                                                                        external={true}
                                                                                        href="https://survey.zohopublic.com/zs/qjCCpr"
                                                                                        buttonColor="teal"
                                                                                        action={() => (reportEvent('outbound:cancellation_survey'))}
                                                                                        setting="button_no_transform" />
                                                                                </>
                                                                            </Layout>}
                                                                    </Layout>}
                                                            </Layout>
                                                        </Layout>
                                                    </Layout>);
                                            }))) :
                            <Layout padding={{bottom: 1}}>
                                <Copy setting="small">Your account has no Relays eligible for cancellation</Copy>
                            </Layout>);
                }
            })));
